<script setup lang="ts">
import { FaqProps, MenuItem } from "./Faq.props"

defineProps<FaqProps>()

/**
 * GTM Customer Help Events
 */
const { event, sendEvent } = useGAEvent(`track_customer_help`, "custom_event")
const router = useRouter()

const lastClicked = ref()
const isLoading = ref(true)

onMounted(() => {
  isLoading.value = false
})

// As per TF-2894, we use this to fix a weird scrolling behavior (on any faq question the scroll was going on top then scrolling to the specific faq, instead of using the current position and then scroll)
router.options.scrollBehavior = (to, from, savedPosition) => {
  if (savedPosition) return savedPosition
  if (to.hash) return { el: to.hash, behavior: "smooth" }
  return false // it prevents auto-scroll
}

/**
 * @description Anchor the page to the fAQ id and send the GTM event
 * @param {MenuItem} entry
 */
const clickOnAccorion = (entry: MenuItem) => {
  const { title, id } = entry

  id ? navigateTo(`#question-${id}`) : null

  if (lastClicked.value != id) {
    event.value = {
      event: "customer_help",
      action: "faq",
      category: "faq",
      label: title
    }
    sendEvent({ clearEcommerce: false })
    lastClicked.value = id
  } else {
    lastClicked.value = null
  }
}
</script>

<template>
  <div class="faq-wrapper flex flex-col gap-14">
    <div
      :id="`${category?.toLowerCase()}`"
      v-if="faqs"
      v-for="({ category, questionAndAnswers }, index) in faqs"
      :key="`faqSection${index}`"
      class="flex flex-col gap-6 text-left"
    >
      <p
        v-if="category"
        :id="category.toLowerCase()"
        class="elephant-bold text-green-main"
      >
        {{ category }}
      </p>

      <div
        v-if="isLoading"
        v-for="(question, index) in 8"
        :key="`question-${index}`"
        class="
          hidden-no-script
          border-black-10
          flex
          items-center
          justify-between
          border-b
          pb-6
        "
      >
        <SkeletonAnimatedPlaceholder
          height="35px"
          :width="Math.floor(Math.random() * 50) + 400 + 'px'"
          borderRadius="8px"
        />

        <UtilsIcon name="ChevronDown.svg" class="h-6 w-6" />
      </div>
      <AppAccordion
        v-if="questionAndAnswers"
        :id="id ? `question-${id}` : ''"
        v-for="({ title, text, id }, index) in questionAndAnswers"
        :key="`menuItem${index}`"
        class="
          custom-accordion-faqs
          show-no-script
          border-black-10
          text-black-80
          flex flex-col
          gap-2
          border-b
          pb-6
          text-left
        "
        :class="{ hidden: isLoading }"
        :text="title"
        :is-open="null"
        with-icons
        @click="() => clickOnAccorion({ title, text, id })"
      >
        <template #icon-to-open>
          <UtilsIcon name="ChevronDown.svg" class="h-6 w-6" />
        </template>

        <template #icon-to-close>
          <UtilsIcon name="ChevronUp.svg" class="h-6 w-6" />
        </template>

        <template #body>
          <UtilsMarkdown
            v-if="text"
            :content="text"
            class="custom-accordion-faqs"
            tag="div"
          />
        </template>
      </AppAccordion>
    </div>
  </div>
</template>

<style lang="scss">

html {
  scroll-behavior: smooth;
}

</style>
